import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ClipLoader } from 'react-spinners';
import { PaymentType, ShippingAddress, SubscriptionService } from '@dayetopia/types';
import { useRecurly } from '@recurly/react-recurly';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { styled } from 'styled-components';
import { Context } from '@components/Context';
import ApplePayButton from '@components/shared/ApplePayButton';
import ErrorMessage from '@components/shared/ErrorMessage';
import { CartItem } from '@contracts';
import { signInAsGuest } from '@utils/account';
import { getDeliveryValidationSchema } from '@utils/addressValidation';
import { getApplePayLineItems, getGooglePayLineItems } from '@utils/cart';
import { convertApplePayAddressToRecurly, convertGooglePayAddressToRecurly } from '@utils/helpers';
import { calculateShippingCost } from '@utils/shippingCost';
import { trackEvent } from '@utils/tracking';
import useCheckout from '@utils/useCheckout';
import Colors from './Colors';
import { isDevelopmentEnvironment } from '@utils/environment';

interface Props {
  cartItems: CartItem[];
  price: number;
  coupon?: string;
}

const ExpressCheckoutButtons: React.FC<Props> = ({ cartItems, price, coupon = '' }) => {
  const { t } = useTranslation();
  const { state, dispatch } = useContext(Context);
  const { currency, country, firebaseAuth, freeShippingRequirement, shippingFees, tax } = state;
  const googlePayButtonRef = useRef<HTMLDivElement>(null);
  const [applePayReady, setApplePayReady] = useState(false);
  const [googlePayReady, setGooglePayReady] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorList, setErrorList] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const recurly = useRecurly();
  const subtotal = price - (tax || 0);
  const shippingCost = calculateShippingCost({ price, freeShippingRequirement, cartItems, shippingFees });
  const totalPrice = subtotal + shippingCost.shippingPrice;
  const applePayTotal = { label: 'Daye', amount: totalPrice.toFixed(2) };

  const setErrorMessageAndHideItAfter5Seconds = (error: string) => {
    setErrorMessage(error);
    setTimeout(() => {
      setErrorMessage('');
    }, 5000);
  };

  const validateAddress = async (address: ShippingAddress): Promise<string | null> => {
    try {
      const addressValidationSchema = getDeliveryValidationSchema(t as (key: string, params?: object) => string);
      await addressValidationSchema.validate(address);
      return null;
    } catch (error) {
      return error.message;
    }
  };

  const handleAddressValidation = async (deliveryAddressObject: ShippingAddress): Promise<boolean> => {
    const addressError = await validateAddress(deliveryAddressObject);
    if (addressError) {
      setErrorMessage(addressError);
      setLoading(false);
      return false;
    }
    return true;
  };

  const applePay = useMemo(() => {
    if (isDevelopmentEnvironment()) return null; // disable apple pay on development
    return recurly.ApplePay({
      country,
      currency: currency.letterCode,
      label: 'Daye',
      paymentRequest: {
        total: applePayTotal,
        lineItems: getApplePayLineItems(cartItems, shippingCost.shippingPrice, tax || 0),
        requiredShippingContactFields: ['postalAddress', 'name', 'phone', 'email'],
        merchantCapabilities: ['supports3DS']
      }
    });
  }, [price, currency.letterCode]);

  const googlePay = useMemo(
    () =>
      recurly.GooglePay({
        googleMerchantId: process.env.GOOGLE_MERCHANT_ID,
        billingAddressRequired: true,
        buttonOptions: {
          buttonType: 'plain',
          buttonSizeMode: 'fill'
        },
        paymentDataRequest: {
          shippingAddressRequired: true,
          emailRequired: true,
          transactionInfo: {
            currencyCode: currency.letterCode,
            countryCode: country,
            totalPrice: totalPrice?.toFixed(2),
            totalPriceLabel: 'Total',
            totalPriceStatus: 'FINAL',
            displayItems: getGooglePayLineItems(cartItems, shippingCost.shippingPrice, tax || 0)
          }
        }
      }),
    [price, currency.letterCode]
  );

  useEffect(() => {
    if (!applePay) return;

    applePay.ready(() => {
      setApplePayReady(true);
    });
    applePay.on('error', (err) => {
      console.error('Apple Pay error', err);
    });
    applePay.on('cancel', () => {
      setLoading(false);
    });
    applePay.on('paymentAuthorized', (event) => {
      console.debug('Apple Pay succeeded.', event);
      setLoading(true);
      handleCartItemsAdd();
      const { recurlyToken: token, shippingContact } = event.payment;

      (async () => {
        try {
          if (!firebaseAuth?.currentUser) {
            await signInAsGuest(firebaseAuth);
          }

          const deliveryAddressObject = convertApplePayAddressToRecurly(shippingContact);

          if (!(await handleAddressValidation(deliveryAddressObject))) {
            return;
          }

          await handleCheckout({
            tokenId: token.id,
            coupon,
            subscriptionServiceUsed: SubscriptionService.Recurly,
            paymentType: PaymentType.ApplePay,
            shippingAddress: deliveryAddressObject,
            isPayPalOrderPreview: false,
            primaryPaymentMethod: false,
            expressCartItems: cartItems,
            email: shippingContact.emailAddress
          });
        } catch (err: any) {
          console.error(err);
          trackEvent('payment_error_applepay');
          setLoading(false);
        }
      })();
    });
  }, [applePay]);

  useEffect(() => {
    setGooglePayReady(!loading);
  }, [loading]);

  useEffect(() => {
    googlePay.on('ready', (googlePayButton) => {
      setGooglePayReady(true);
      const container = googlePayButtonRef.current;
      if (container) {
        container.innerHTML = '';
        container.appendChild(googlePayButton);
      }
    });
    googlePay.on('error', (err) => {
      console.error('Google Pay error', err);
    });
    googlePay.on('paymentAuthorized', (event) => {
      console.debug('Google Pay succeeded.', event);
      setLoading(true);
      handleCartItemsAdd();
      const { recurlyToken: token, shippingAddress, email } = event;
      (async () => {
        try {
          if (!firebaseAuth?.currentUser) {
            await signInAsGuest(firebaseAuth);
          }

          const deliveryAddressObject = convertGooglePayAddressToRecurly(shippingAddress);

          if (!(await handleAddressValidation(deliveryAddressObject))) {
            return;
          }

          await handleCheckout({
            tokenId: token.id,
            coupon,
            subscriptionServiceUsed: SubscriptionService.Recurly,
            paymentType: PaymentType.GooglePay,
            shippingAddress: deliveryAddressObject,
            isPayPalOrderPreview: false,
            primaryPaymentMethod: false,
            expressCartItems: cartItems,
            email
          });
        } catch (err: any) {
          console.error(err);
          trackEvent('payment_error_gpay');
          setLoading(false);
        }
      })();
    });
  }, [googlePay, googlePayButtonRef, price]);

  const handleCartItemsAdd = () => {
    dispatch({ type: 'cartClear' });
    cartItems.forEach((cartItem) => {
      dispatch({
        type: 'cartItemAdd',
        data: cartItem
      });
    });
  };

  const handleApplePayClick = () => {
    if (!applePay) return;
    applePay.begin();
  };

  const { handleCheckout } = useCheckout({
    setLoading,
    setErrorMessage: setErrorMessageAndHideItAfter5Seconds,
    setErrorList,
    handle3DS: () => null
  });

  return (
    <Container>
      {loading ? (
        <LoadingWrap>
          <ClipLoader color={Colors.navyBlue} size={20} speedMultiplier={0.5} />
        </LoadingWrap>
      ) : null}
      {applePayReady && <ApplePayButton onClick={handleApplePayClick} $show={applePayReady} />}
      <GooglePayButtonContainer ref={googlePayButtonRef} $show={googlePayReady} />
      {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
    </Container>
  );
};

export default ExpressCheckoutButtons;

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const GooglePayButtonContainer = styled.div<{ $show: boolean }>`
  height: 40px;
  width: 100%;
  display: ${(props) => (props.$show ? 'flex' : 'none')};

  & .gpay-button {
    background-repeat: no-repeat;
    background-position: center;
    border-radius: 2px;

    .gpay-card-info-animation-container {
      border-radius: 2px;
    }
  }

  .gpay-card-info-container {
    border-radius: 2px;
  }
`;

const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
